import { useEffect, useState } from "react";
import { ExternalLink, Star } from "lucide-react";
import Reveal from "@/components/Reveal";
import { SITE } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ rating }) {
  return (
    <div className="flex gap-0.5" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-4 w-4 ${
            n <= (rating || 0)
              ? "fill-terracotta text-terracotta"
              : "text-cream-border"
          }`}
        />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/reviews`)
      .then((r) => r.json())
      .then((d) =>
        setData(d.reviews && d.reviews.length ? d : { reviews: [] })
      )
      .catch(() => setData({ reviews: [] }));
  }, []);

  if (!data || !data.reviews.length) return null;

  return (
    <section
      id="resenas"
      className="border-t border-cream-border bg-cream-soft py-24 lg:py-32"
      data-testid="reviews-section"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <p className="eyebrow">Lo que dicen nuestros clientes</p>
            <h2 className="mt-5 max-w-xl font-serif text-4xl leading-tight tracking-tight text-olive sm:text-5xl lg:text-6xl">
              Cocinas entregadas, clientes felices.
            </h2>
          </Reveal>
          {data.source === "google" && (
            <Reveal delay={0.15} className="lg:text-right">
              <p className="font-serif text-6xl text-olive">
                {data.rating ?? "—"}
              </p>
              <div className="mt-2 flex lg:justify-end">
                <Stars rating={Math.round(data.rating || 0)} />
              </div>
              <p className="mt-2 text-sm text-olive-mid" data-testid="reviews-count">
                {data.user_rating_count ?? 0} reseñas en Google
              </p>
            </Reveal>
          )}
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {data.reviews.slice(0, 6).map((r, i) => (
            <Reveal key={i} delay={i * 0.08} y={24}>
              <article
                data-testid="review-card"
                className="flex h-full flex-col rounded-3xl border border-cream-border bg-card p-8"
              >
                <Stars rating={r.rating} />
                <p className="mt-5 flex-1 leading-relaxed text-olive">
                  “{r.text}”
                </p>
                <div className="mt-6 flex items-center justify-between border-t border-cream-border pt-4">
                  <div>
                    <p className="font-medium text-olive">{r.author}</p>
                    <p className="text-xs text-olive-mid">
                      {r.time || "Reseña de Google"}
                    </p>
                  </div>
                  {r.uri && (
                    <a
                      href={r.uri}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="review-google-link"
                      aria-label="Ver reseña en Google Maps"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-olive/15 text-olive transition-colors duration-300 hover:bg-olive hover:text-cream"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-10">
          <p className="text-xs text-olive-mid">
            Reseñas de Google · se muestran en orden de relevancia ·{" "}
            <a
              href={SITE.googleBusiness}
              target="_blank"
              rel="noreferrer"
              data-testid="reviews-cta-link"
              className="underline underline-offset-4 transition-colors duration-200 hover:text-terracotta"
            >
              Déjanos la tuya
            </a>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
